import { memo } from "react";
import { bool, number, string, func, oneOfType, shape, node } from "prop-types";
import { ClearInput, ValueDivider } from "@components";
import classnames from "classnames";

export const ValueCellEditableContent = memo(({
  value,
  valueMax,
  onValueChange,
  displayMax,
  symbolStart,
  symbolEnd,
  showDecimals,
  emptyInputs,
  messageTitle,
  messageContent,
  displayMinDialogMessage = true,
  displayMaxDialogMessage = true,
  isSmall,
  min = 0,
  max = 9999,
  warning,
  className,
  displayMinMaxSeparator = true,
  fullWidth,
  allowNegative,
  americanFormat,
  applySuffixSpacing,
  decimals,
  inputClassName,
  placeholderMin,
  placeholderMax,
  onFocus,
  navigationKey,
}) => {
  const commonProps = {
    americanFormat,
    applySuffixSpacing,
    allowNegative,
    onFocus,
    showDecimals,
    decimals,
    prefix: emptyInputs ? undefined : symbolStart,
    suffix: emptyInputs ? undefined : symbolEnd,
    color: warning ? "warning" : "primary",
    fullWidth: fullWidth || displayMax,
    size: isSmall ? "small" : "large",
    tabIndex: messageContent ? -1 : undefined,
  }

  const handleValueChange = (isMax) => (value) => (
    onValueChange && onValueChange(value, isMax)
  )

  return (
    <>
      <ClearInput
        {...commonProps}
        value={emptyInputs ? undefined : value}
        onChange={handleValueChange(false)}
        alignContent={displayMax ? "right" : "center"}
        min={typeof min === "number" ? min : min?.min}
        max={typeof max === "number" ? max : max?.min}
        className={classnames(displayMax ? "input-min" : "input-avg", className, inputClassName)}
        placeholder={emptyInputs ? undefined : placeholderMin}
        navigationKey={navigationKey ? `${navigationKey}-min` : undefined}
        clickDialogMessageContent={displayMinDialogMessage ? messageContent : undefined}
        clickDialogMessageTitle={displayMinDialogMessage ? messageTitle : undefined}
      />
      {
        displayMax &&
        <ValueDivider display={displayMinMaxSeparator} />
      }
      {
        displayMax &&
        <ClearInput
          {...commonProps}
          value={emptyInputs ? undefined : valueMax}
          onChange={handleValueChange(true)}
          alignContent="left"
          min={typeof min === "number" ? min : min?.max}
          max={typeof max === "number" ? max : max?.max}
          className={classnames("input-max", className, inputClassName)}
          placeholder={emptyInputs ? undefined : placeholderMax}
          navigationKey={navigationKey ? `${navigationKey}-max` : undefined}
          clickDialogMessageContent={displayMaxDialogMessage ? messageContent : undefined}
          clickDialogMessageTitle={displayMaxDialogMessage ? messageTitle : undefined}
        />
      }
    </>
  );
});

ValueCellEditableContent.propTypes = {
  value: oneOfType([number, string]),
  valueMax: oneOfType([number, string]),
  onValueChange: func,
  displayMax: bool,
  displayMinMaxSeparator: bool,
  symbolStart: string,
  symbolEnd: oneOfType([node, string]),
  placeholder: oneOfType([string, number]),
  warning: bool,
  isSmall: bool,
  fullWidth: bool,
  emptyInputs: bool,
  min: oneOfType([number, shape({ min: number, max: number })]),
  max: oneOfType([number, shape({ min: number, max: number })]),
  // common
  showDecimals: bool,
  allowNegative: bool,
  className: string,
  messageTitle: string,
  messageContent: string,
  decimals: number,
  inputClassName: string,
  onFocus: func,
  placeholderMin: oneOfType([number, string]),
  placeholderMax: oneOfType([number, string]),
  navigationKey: string,
  americanFormat: bool,
  applySuffixSpacing: bool,
  displayMinDialogMessage: bool,
  displayMaxDialogMessage: bool,
};
